import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Chip,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SendIcon from '@mui/icons-material/Send'
import { apiClient } from '../services/api'

interface Lender {
    Id: string
    Name: string
    IsActive: boolean
    SubmitEndpoint: string | null
    IntegrationTypeName: string | null
    AuthTypeName: string | null
}

interface Application {
    id: string
    description: string
    statusName: string
    businessName: string
}

interface SubmissionResult {
    lenderId: string
    lenderName: string
    success: boolean
    statusCode?: number
    errorMessage?: string
    warning?: string
}

export default function SubmitToLendersPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [application, setApplication] = useState<Application | null>(null)
    const [lenders, setLenders] = useState<Lender[]>([])
    const [selectedLenders, setSelectedLenders] = useState<Set<string>>(new Set())
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [submissionResults, setSubmissionResults] = useState<SubmissionResult[] | null>(null)

    useEffect(() => {
        fetchData()
    }, [id])

    const fetchData = async () => {
        try {
            setLoading(true)
            const [appData, lendersData] = await Promise.all([
                apiClient.getApplicationById(id!),
                apiClient.getLenders(),
            ])

            setApplication({
                id: appData.Id,
                description: appData.Description,
                statusName: appData.StatusName,
                businessName: appData.Business?.LegalName || appData.Business?.DBAName || 'Unknown',
            })
            setLenders(lendersData)
            setError(null)
        } catch (err) {
            setError('Failed to load data')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleLenderToggle = (lenderId: string) => {
        const newSelected = new Set(selectedLenders)
        if (newSelected.has(lenderId)) {
            newSelected.delete(lenderId)
        } else {
            newSelected.add(lenderId)
        }
        setSelectedLenders(newSelected)
    }

    const handleSelectAll = () => {
        if (selectedLenders.size === lenders.length) {
            setSelectedLenders(new Set())
        } else {
            setSelectedLenders(new Set(lenders.map(l => l.Id)))
        }
    }

    const handleSubmit = async () => {
        if (selectedLenders.size === 0) {
            setError('Please select at least one lender')
            return
        }

        try {
            setSubmitting(true)
            setError(null)
            const response = await apiClient.submitToLenders(id!, Array.from(selectedLenders))
            setSubmissionResults(response.Results)
        } catch (err) {
            setError('Failed to submit to lenders')
            console.error(err)
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <Container maxWidth="lg">
                <CircularProgress />
            </Container>
        )
    }

    if (error && !application) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error}</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/applications/${id}`)}>
                    Back to Application
                </Button>
            </Box>

            <Typography variant="h4" component="h1" gutterBottom>
                Submit Application to Lenders
            </Typography>

            {application && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>Application Details</Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>ID:</strong> {application.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Business:</strong> {application.businessName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Description:</strong> {application.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Status:</strong> {application.statusName}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {!submissionResults ? (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Select Lenders</Typography>
                        <Button onClick={handleSelectAll} size="small">
                            {selectedLenders.size === lenders.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </Box>

                    <FormGroup sx={{ maxHeight: 'calc(100vh - 500px)', overflowY: 'auto', pr: 1 }}>
                        {lenders.map(lender => (
                            <FormControlLabel
                                key={lender.Id}
                                control={
                                    <Checkbox
                                        checked={selectedLenders.has(lender.Id)}
                                        onChange={() => handleLenderToggle(lender.Id)}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography>{lender.Name}</Typography>
                                        {lender.IntegrationTypeName && (
                                            <Chip label={lender.IntegrationTypeName} size="small" variant="outlined" />
                                        )}
                                        {lender.AuthTypeName && (
                                            <Chip label={lender.AuthTypeName} size="small" color="primary" />
                                        )}
                                        {!lender.SubmitEndpoint && (
                                            <Chip label="No Endpoint" size="small" color="warning" />
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </FormGroup>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                            onClick={handleSubmit}
                            disabled={submitting || selectedLenders.size === 0}
                        >
                            {submitting ? 'Submitting...' : `Submit to ${selectedLenders.size} Lender${selectedLenders.size !== 1 ? 's' : ''}`}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate(`/applications/${id}`)} disabled={submitting}>
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Submission Results
                    </Typography>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Lender</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Status Code</TableCell>
                                    <TableCell>Message</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {submissionResults.map((result, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{result.lenderName}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={result.success ? 'Success' : 'Failed'}
                                                color={result.success ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{result.statusCode || '-'}</TableCell>
                                        <TableCell>
                                            {result.errorMessage || result.warning || 'Submitted successfully'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button variant="contained" onClick={() => navigate(`/applications/${id}`)}>
                            Back to Application
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSubmissionResults(null)
                                setSelectedLenders(new Set())
                            }}
                        >
                            Submit to More Lenders
                        </Button>
                    </Box>
                </Paper>
            )}
        </Container>
    )
}
